import { TFunction } from 'i18next'
import { isEmpty, isUndefined } from 'lodash'
import { NavigateFunction } from 'react-router-dom'
import { Dispatch } from 'redux'
import { setActiveInstallation } from 'src/State/Storage/StorageActions'
import { ApiAccountType } from '../api/types'
import { getInstallationDashboardUrl, getInstallationDashboardUrlByIds } from '../dashboard/routes'
import { installationPaths } from '../flow/advanced-settings/constants'
import { DeviceLinkModel } from './models'
import { Installation } from './models/installation.model'
import { InstallationViewType, SystemLogFilterWithoutSetter } from './types'

export const getProjectName = (installation: Installation, t: TFunction): string => {
  if (!installation.projectName) return t('installation.overview.noName')
  return installation.projectName
}

export const getOwnerName = (installation: Installation): string => {
  if (installation.ownerAccountType === ApiAccountType.company && installation.ownerCompanyName) {
    return installation.ownerCompanyName
  }

  if (installation.ownerFirstName || installation.ownerLastName) {
    return `${installation.ownerFirstName ?? ''} ${installation.ownerLastName ?? ''}`
  }

  return '---'
}

export const getInstallerName = (installation: Installation): string => {
  if (!installation.createdByFirstName && !installation.createdByLastName) return '---'
  return `${installation.createdByFirstName ?? ''} ${installation.createdByLastName ?? ''}`
}

export const getProjectInfo = (installation: Installation, t: TFunction): string => {
  const projectInfo = [`${t('common.id')} ${installation.id}`]
  if (installation.ownerFirstName && installation.ownerLastName) {
    projectInfo.push(`, ${installation.ownerFirstName} ${installation.ownerLastName}`)
  }
  if (installation.streetName && installation.streetNumber && installation.city) {
    projectInfo.push(`, ${installation.streetName} ${installation.streetNumber}, ${installation.city}`)
  }
  return projectInfo.join()
}

export const getInstallationConfigurationUrl = (installation: Installation): string => {
  return `/installation/${installation.id}/configuration/general`
}

export const getToAdvancedSettingsLink = (installation: Installation): string => {
  if (installation.emsMasterDeviceId) {
    return `/installation/${installation.id}/${installation.emsMasterDeviceId}/properties/advanced-settings/${installationPaths.General}`
  }
  return `/installation/${installation.id}/properties/advanced-settings/${installationPaths.General}`
}

/**
 * Updates the active installation and storageId in Redux, making them available across the application.
 * storageId is explicitly set to null when not available — this is an expected case for installations without
 * a storage device (e.g. installations with status "in planning"). Without the explicit null,
 * switching from a normal installation to a planned one would leave the previous storageId in state.
 */
export const setActiveInstallationAndStorage = <U extends { id: number; emsMasterDeviceId?: string }>(installation: U, dispatch: Dispatch): void => {
  dispatch(setActiveInstallation(installation.id, installation.emsMasterDeviceId ?? null))
}

/**
 * This hooks provides the installation view (cards vs table)
 */
export const getCardVsTableView = (installationCount: number): InstallationViewType => {
  const BREAKPOINT = 5 // 5 or more installations are displayed via table. Less via card.

  if (installationCount < BREAKPOINT) {
    return 'installation-card-view'
  } else {
    return 'installation-table-view'
  }
}

//TODO: as this is using 2 hooks (dispatch, history) it could make sense to move this function also into a hook,
// remove both as params and load them as hooks instead directly.
// would reduce the param- and so the overall complexity of this function.
export const onSelectInstallation = (dispatch: Dispatch, navigate: NavigateFunction, installation?: Installation): void => {
  if (installation?.installationStatus === 'planned') {
    dispatch(setActiveInstallation(installation.id))
    navigate(getInstallationDashboardUrlByIds(installation.id))
  } else if (installation?.emsMasterDeviceId) {
    dispatch(setActiveInstallation(installation.id, installation.emsMasterDeviceId))
    navigate(getInstallationDashboardUrl(installation))
  }
}

//TODO: as this is using 2 hooks (dispatch, history) it could make sense to move this function also into a hook,
// remove both as params and load them as hooks instead directly.
// would reduce the param- and so the overall complexity of this function.
export const onEditInstallation = (dispatch: Dispatch, navigate: NavigateFunction, installation?: Installation): void => {
  if (installation) {
    dispatch(setActiveInstallation(installation.id, installation.emsMasterDeviceId))
    navigate(`/installation/${installation.id}/${installation.emsMasterDeviceId}/properties`)
  }
}

export const getInstalledInstallations = (installations: Installation[]): Installation[] => {
  return installations.filter(installation => installation.installationStatus !== 'incomplete')
}

interface ActiveFilter {
  filter?: string
  sort?: string
}

const getFilterValue = <T>(array: T[]): string => {
  if (array.length >= 1) {
    return `(${array.join(',')})`
  }
  return ''
}

/**
 * This function turns an array of active filters into a Apache Lucene Query Syntax filter
 * and a sort parameter to sort the results
 * @param activeFilters
 * @returns filter and sort for a authFetch payload parameter
 */
export const parseActiveFilter = (activeFilters: SystemLogFilterWithoutSetter, deviceLinks: DeviceLinkModel[]): ActiveFilter => {
  const result: ActiveFilter = {}
  const filters = [] as string[]
  const { sortFilter, statusFilter, typeFilter, clientFilter } = activeFilters
  result.sort = `${sortFilter.activeValue}${sortFilter.fieldName}`

  const storagesMappedToDeviceLinkIds = clientFilter.activeValue
    .map(value => deviceLinks.find(deviceLink => deviceLink.deviceId && Number(deviceLink.deviceId) === value)?.id)
    .filter(id => !isUndefined(id))

  if (statusFilter && !isEmpty(statusFilter.activeValue)) {
    filters.push(`${statusFilter.fieldName}:IN${getFilterValue(statusFilter.activeValue)}`)
  }

  if (typeFilter && !isEmpty(typeFilter.activeValue)) {
    filters.push(`${typeFilter.fieldName}:IN${getFilterValue(typeFilter.activeValue)}`)
  }

  if (clientFilter && !isEmpty(storagesMappedToDeviceLinkIds)) {
    const filterValue = getFilterValue(storagesMappedToDeviceLinkIds.map(id => `${id}`))
    filters.push(`${clientFilter.fieldName}:IN${filterValue}`)
  }

  result.filter = filters.join(' AND ')

  return result
}
